import { useCallback, useEffect, useRef, useState } from "react";
import { isRequestCancellation } from "@shared/api/client";

export function useIso900114001DebouncedValue<T>(value: T, delay = 300): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedValue(value), delay);
    return () => window.clearTimeout(timer);
  }, [delay, value]);

  return debouncedValue;
}

export function useIso900114001LatestRequest() {
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestSequenceRef = useRef(0);
  const lastLoadedKeyRef = useRef("");

  useEffect(() => () => {
    abortControllerRef.current?.abort();
  }, []);

  const resetRequestKey = useCallback(() => {
    lastLoadedKeyRef.current = "";
  }, []);

  const runLatest = useCallback(async <T,>(
    requestKey: string,
    request: (signal: AbortSignal) => Promise<T>,
    force = false,
  ): Promise<T | undefined> => {
    if (!force && requestKey === lastLoadedKeyRef.current) {
      return undefined;
    }

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const sequence = ++requestSequenceRef.current;

    try {
      const result = await request(controller.signal);
      if (sequence !== requestSequenceRef.current) {
        return undefined;
      }
      lastLoadedKeyRef.current = requestKey;
      return result;
    } catch (error) {
      if (isRequestCancellation(error) || sequence !== requestSequenceRef.current) {
        return undefined;
      }
      throw error;
    } finally {
      if (sequence === requestSequenceRef.current && abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, []);

  const hasActiveRequest = useCallback(() => abortControllerRef.current !== null, []);

  return { runLatest, resetRequestKey, hasActiveRequest };
}
